import { PageLoadPerformanceEventProperties, PerformanceEventName } from '@ms/yammer-telemetry-support';

import { removeCachedAppLoadHashFromUrl } from './cachedAppLoadUrlHash';
import {
  clearCompletedPerformanceEventMeasure,
  endAndReportPageLoadPerformanceMeasureNowIfNotCompleted,
  scheduleEndAndReportPageLoadPerformanceMeasureAfterStyleAndLayout,
  startPageLoadPerformanceMeasure,
  updatePageLoadPerformanceMeasureEventProperties,
} from './pageLoadTelemetryReporting';

interface PageLoadTimingEventWithLoadIdOptions {
  readonly pageLoadId: number;
  readonly eventProperties: PageLoadPerformanceEventProperties;
}
type PageLoadTimingEventWithLoadId = (props: PageLoadTimingEventWithLoadIdOptions) => void;

export const startPageLoadTiming: PageLoadTimingEventWithLoadId = ({ pageLoadId, eventProperties }) => {
  startPageLoadPerformanceMeasure({
    eventName: PerformanceEventName.PageLoad,
    pageLoadId,
    eventProperties,
  });
};

export const updatePageLoadTimingEvent: PageLoadTimingEventWithLoadId = ({ pageLoadId, eventProperties }) => {
  updatePageLoadPerformanceMeasureEventProperties({
    eventName: PerformanceEventName.PageLoad,
    pageLoadId,
    eventProperties,
  });
};

export const endAndReportPageLoadTimingEventAfterStyleAndLayout: PageLoadTimingEventWithLoadId = ({
  pageLoadId,
  eventProperties,
}) => {
  scheduleEndAndReportPageLoadPerformanceMeasureAfterStyleAndLayout({
    eventName: PerformanceEventName.PageLoad,
    pageLoadId,
    eventProperties,
    endedWithError: false,
  });
};

export const cancelPageLoadTimingEvent: PageLoadTimingEventWithLoadId = ({ pageLoadId, eventProperties }) => {
  endAndReportPageLoadPerformanceMeasureNowIfNotCompleted({
    eventName: PerformanceEventName.PageLoad,
    pageLoadId,
    eventProperties,
    endedWithError: false,
    interrupted: true,
  });
};

interface ClearCachedAppPageLoadTrackingProps {
  readonly isRoutelessApp: boolean;
}
type ClearCachedAppPageLoadTracking = (props: ClearCachedAppPageLoadTrackingProps) => void;
export const clearCachedAppPageLoadTracking: ClearCachedAppPageLoadTracking = ({ isRoutelessApp }) => {
  if (isRoutelessApp) {
    return;
  }

  history.replaceState(null, '', removeCachedAppLoadHashFromUrl(location.href));
};

const cachedAppPageLoadId = -1;

export const startCachedAppPageLoadTiming = () => {
  cancelCachedAppPageLoadTiming();
  clearCompletedPerformanceEventMeasure({
    eventName: PerformanceEventName.CachedFirstPageLoad,
    pageLoadId: cachedAppPageLoadId,
  });

  startPageLoadPerformanceMeasure({
    eventName: PerformanceEventName.CachedFirstPageLoad,
    pageLoadId: cachedAppPageLoadId,
  });
};

interface EndCachedAppPageLoadTimingEventOptions {
  readonly eventProperties: PageLoadPerformanceEventProperties;
}
type EndCachedAppPageLoadTimingEvent = (props?: EndCachedAppPageLoadTimingEventOptions) => void;

export const endCachedAppPageLoadTimingEventAfterStyleAndLayout: EndCachedAppPageLoadTimingEvent = (props) => {
  scheduleEndAndReportPageLoadPerformanceMeasureAfterStyleAndLayout({
    eventName: PerformanceEventName.CachedFirstPageLoad,
    pageLoadId: cachedAppPageLoadId,
    eventProperties: props?.eventProperties,
    endedWithError: false,
  });
};

export const cancelCachedAppPageLoadTiming: EndCachedAppPageLoadTimingEvent = (props) => {
  endAndReportPageLoadPerformanceMeasureNowIfNotCompleted({
    eventName: PerformanceEventName.CachedFirstPageLoad,
    pageLoadId: cachedAppPageLoadId,
    eventProperties: props?.eventProperties,
    interrupted: true,
    endedWithError: false,
  });
};
